import { Box, Fade, Hidden, Typography } from '@mui/material';
import { useRef, useState } from 'react';

import {
  CardFlair,
  CardFlairVariants,
  CreditsPill,
  DurationPill,
  EthicsPill,
  Skeleton,
} from '@/components';
import { ComplianceTypes, Routes } from '@/constants';
import { AnalyticsService, EVENT_CONSTANTS, EVENTS } from '@/services';
import { createRouteWithParams, getTrailerPreviewDelay } from '@/utils';

import { Link } from '../../../Link';
import {
  EpisodeCardJumboDimensions,
  EpisodeCardVariants,
} from '../../constants';
import { episodeBannerStyles as styles } from '../../styles';
import { EpisodeBannerComponentProps } from '../../useEpisodeBanner';
import { BookmarkButton } from '../BookmarkButton';
import { PlayButton } from '../PlayButton';
import { PreviewButton } from '../PreviewButton';
import { TrailerPreviewPlayer } from '../TrailerPreviewPlayer';

interface EpisodeCardJumboProps extends EpisodeBannerComponentProps {
  onLinkClick?: () => void;
}

const MINIMUM_HOVER_DURATION_FOR_ANALYTICS = 500;

export const EpisodeCardJumbo = ({
  episode,
  onLinkClick,
  isEpisodeEthics,
  activeEpisodeId,
  impressionRef,
  referrer = {},
  compliance,
  isTrailerPreviewEnabled,
}: EpisodeCardJumboProps) => {
  const [coverLoaded, setCoverLoaded] = useState(false);
  const [isTrailerPreviewOpen, setIsTrailerPreviewOpen] = useState(false);
  const [hoverStartTimestamp, setHoverStartTimestamp] = useState<number>();
  const [isTrailerPreviewEnded, setIsTrailerPreviewEnded] = useState(false);
  const [isTrailerPreviewLoaded, setIsTrailerPreviewLoaded] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const location = {
    ...referrer,
    component: EVENT_CONSTANTS.COMPONENT.EPISODE_CARD,
    variants: {
      ...referrer.variants,
      episodeCardVariant: EpisodeCardVariants.JUMBO,
    },
  };

  const isCPE = compliance === ComplianceTypes.CPE;

  const onMouseEnter = () => {
    setHoverStartTimestamp(Date.now());

    timerRef.current = setTimeout(() => {
      setIsTrailerPreviewOpen(true);

      if (episode?.episodeId) {
        AnalyticsService.logEvent(EVENTS.EPISODE_EVENTS.MEANINGFUL_HOVER, {
          episodeId: episode.episodeId,
          referrer: location,
        });
      }
    }, getTrailerPreviewDelay());
  };

  const onMouseLeave = () => {
    if (hoverStartTimestamp && episode?.episodeId) {
      const hoverDuration = Date.now() - hoverStartTimestamp;
      if (hoverDuration > MINIMUM_HOVER_DURATION_FOR_ANALYTICS) {
        AnalyticsService.logEvent(EVENTS.EPISODE_EVENTS.HOVER, {
          episodeId: episode.episodeId,
          hoverDuration: Date.now() - hoverStartTimestamp,
          referrer: location,
        });
      }
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    setIsTrailerPreviewOpen(false);
    setIsTrailerPreviewEnded(false);
    setIsTrailerPreviewLoaded(false);
    setHoverStartTimestamp(undefined);
  };

  return (
    <Box
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        position: 'relative',
        height: EpisodeCardJumboDimensions.height,
        width: EpisodeCardJumboDimensions.width,
      }}>
      <Box
        ref={impressionRef}
        sx={[
          styles.root,
          {
            height: EpisodeCardJumboDimensions.height,
            width: EpisodeCardJumboDimensions.width,
          },
        ]}>
        <Box
          id="episode-cover-img"
          component="img"
          alt="episode cover"
          src={episode.cover}
          onLoad={() => setCoverLoaded(true)}
          sx={[styles.coverImage, { display: coverLoaded ? 'block' : 'none' }]}
        />
        {!coverLoaded && <Skeleton variant="rounded" />}
        <Link
          to={createRouteWithParams(Routes.EPISODE, {
            episodeId: episode.episodeId,
          })}
          onClick={onLinkClick}
          state={{ referrer: location }}
          color="inherit">
          <Box
            sx={[
              styles.gradientContainer,
              {
                background:
                  'linear-gradient(215deg, rgba(0, 0, 0, 0.00) 45%, rgba(0, 0, 0, 0.30) 60%, rgba(0, 0, 0, 0.65) 77%)',
              },
            ]}>
            {!!episode.companyLogo && (
              <Box
                component="img"
                src={episode.companyLogo}
                alt="company logo"
                sx={styles.companyLogo}
              />
            )}
            <Box sx={styles.cardBody}>
              <Box sx={[styles.textContainer, { width: '50%' }]}>
                <Box sx={styles.pillContainer}>
                  {isCPE ? (
                    <CreditsPill credits={episode.credits} color="white" />
                  ) : (
                    <DurationPill duration={episode.duration} color="white" />
                  )}
                  {isEpisodeEthics && !isCPE && <EthicsPill color="white" />}
                </Box>
                <Typography variant="h3" sx={styles.text}>
                  {episode.name}
                </Typography>
                {isCPE && episode.fieldOfStudy ? (
                  <Hidden mdDown>
                    <Typography variant="body3" sx={styles.text}>
                      {episode.fieldOfStudy}
                    </Typography>
                  </Hidden>
                ) : (
                  <Hidden mdDown>
                    <Typography variant="body3" sx={styles.text}>
                      {episode.hook}
                    </Typography>
                  </Hidden>
                )}
              </Box>
              <Box sx={styles.buttons}>
                <PlayButton episode={episode} referrer={location} />
                <PreviewButton episode={episode} referrer={location} />
                <Box sx={styles.buttonsRight}>
                  <BookmarkButton
                    episodeId={episode?.episodeId}
                    referrer={location}
                  />
                </Box>
              </Box>
            </Box>
            {episode.episodeAgeTag === 'new' && (
              <CardFlair variant={CardFlairVariants.LARGE} />
            )}
          </Box>
          {/* 
            decoupling the Fade trigger from the mount trigger
            to avoid a race condition between the Fade animation and
            the video player loading the trailer preview
            on slower internet connections
           */}
          {episode.trailerPreviewUrl &&
            isTrailerPreviewEnabled &&
            isTrailerPreviewOpen &&
            activeEpisodeId !== episode.episodeId && (
              <Fade
                in={isTrailerPreviewLoaded && !isTrailerPreviewEnded}
                timeout={500}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: EpisodeCardJumboDimensions.width,
                    height: EpisodeCardJumboDimensions.height,
                  }}>
                  <TrailerPreviewPlayer
                    episode={episode}
                    referrer={location}
                    onEnded={() => setIsTrailerPreviewEnded(true)}
                    onReady={() => setIsTrailerPreviewLoaded(true)}
                  />
                </Box>
              </Fade>
            )}
        </Link>
      </Box>
    </Box>
  );
};
